import React, { useState, useMemo } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useTheme } from "next-themes";
import { TID } from "../constants/testIds";
import {
  LayoutDashboard, Building2, Store, Battery, Factory, CreditCard,
  Wrench, ShieldCheck, Users, FileBarChart, Bell, Settings,
  ScrollText, LogOut, Sun, Moon, Menu, Zap, ChevronsLeft, Search,
  Radar, Landmark, Layers, PackageOpen, CheckSquare, FolderLock, Radio,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  brand_admin: "Brand Admin",
  outlet_user: "Outlet",
  rushserv_user: "RushServ Engineer",
  manufacturer_user: "Manufacturer",
};

const NAV_ITEMS = [
  { to: "/dashboard",     label: "Dashboard",     icon: LayoutDashboard, tid: TID.navDashboard, roles: "*" },
  { to: "/control-tower", label: "Control Tower", icon: Radar,           tid: "nav-control-tower", roles: ["super_admin"] },
  { to: "/brands",        label: "Brands",        icon: Building2,       tid: TID.navBrands,        roles: ["super_admin", "brand_admin"] },
  { to: "/outlets",       label: "Outlets",       icon: Store,           tid: TID.navOutlets,       roles: ["super_admin", "brand_admin", "outlet_user"] },
  { to: "/assets",        label: "Assets",        icon: Battery,         tid: TID.navAssets,        roles: "*" },
  { to: "/iot-devices",   label: "IoT Devices",   icon: Radio,           tid: "nav-iot-devices",    roles: ["super_admin", "manufacturer_user"] },
  { to: "/manufacturers", label: "Manufacturers", icon: Factory,         tid: TID.navManufacturers, roles: ["super_admin", "manufacturer_user"] },
  { to: "/capital-partners", label: "Capital Partners", icon: Landmark,  tid: "nav-capital",        roles: ["super_admin"] },
  { to: "/asset-pools",   label: "Asset Pools",   icon: Layers,          tid: "nav-pools",          roles: ["super_admin"] },
  { to: "/subscriptions", label: "Subscriptions", icon: CreditCard,      tid: TID.navSubscriptions, roles: ["super_admin", "brand_admin", "outlet_user"] },
  { to: "/maintenance",   label: "Maintenance",   icon: Wrench,          tid: TID.navMaintenance,   roles: "*" },
  { to: "/warranty",      label: "Warranty",      icon: ShieldCheck,     tid: TID.navWarranty,      roles: "*" },
  { to: "/recovery",      label: "Recovery",      icon: PackageOpen,     tid: "nav-recovery",       roles: ["super_admin", "rushserv_user"] },
  { to: "/approvals",     label: "Approvals",     icon: CheckSquare,     tid: "nav-approvals",      roles: "*" },
  { to: "/documents",     label: "Document Vault",icon: FolderLock,      tid: "nav-documents",      roles: "*" },
  { to: "/users",         label: "Users",         icon: Users,           tid: TID.navUsers,         roles: ["super_admin"] },
  { to: "/reports",       label: "Reports",       icon: FileBarChart,    tid: TID.navReports,       roles: ["super_admin", "brand_admin"] },
  { to: "/notifications", label: "Notifications", icon: Bell,            tid: TID.navNotifications, roles: "*" },
  { to: "/audit-logs",    label: "Audit Logs",    icon: ScrollText,      tid: TID.navAuditLogs,     roles: ["super_admin"] },
  { to: "/settings",      label: "Settings",      icon: Settings,        tid: TID.navSettings,      roles: "*" },
];

function crumbFromPath(pathname) {
  if (pathname === "/dashboard") return [{ label: "Dashboard" }];
  const parts = pathname.split("/").filter(Boolean);
  return [{ label: "Home", to: "/dashboard" }, ...parts.map((p) => ({ label: p.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }))];
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const items = useMemo(
    () => NAV_ITEMS.filter((it) => it.roles === "*" || it.roles.includes(user.role)),
    [user.role]
  );

  const doLogout = async () => { await logout(); nav("/", { replace: true }); };
  const crumbs = crumbFromPath(loc.pathname);

  return (
    <div className="min-h-screen bg-background text-foreground flex relative">
      {/* Ambient glow + grid — same visual language as landing */}
      <div className="rushgro-ambient" aria-hidden />
      <div className="rushgro-grid" aria-hidden />
      {/* Sidebar */}
      <aside
        data-testid={TID.sidebar}
        className={`hidden md:flex fixed inset-y-0 left-0 z-30 flex-col border-r bg-card/70 backdrop-blur transition-[width] duration-200 ${collapsed ? "w-16" : "w-60"}`}
      >
        <div className="h-14 flex items-center gap-2 px-4 border-b">
          <div className="w-8 h-8 rounded-lg bg-blue-600 grid place-items-center shrink-0">
            <Zap className="w-5 h-5 text-white" strokeWidth={2.5} />
          </div>
          {!collapsed && (
            <div className="flex flex-col leading-tight">
              <span className="font-heading font-semibold tracking-tight">RushGro</span>
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">PBaaS</span>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/dashboard"}
              data-testid={it.tid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-blue-600 text-white" : "text-foreground/80 hover:bg-muted hover:text-foreground"}`}
              title={it.label}
            >
              <it.icon className="w-4 h-4 shrink-0" />
              {!collapsed && <span>{it.label}</span>}
            </NavLink>
          ))}
        </nav>
        <button
          data-testid={TID.sidebarToggle}
          onClick={() => setCollapsed(!collapsed)}
          className="border-t h-11 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Toggle sidebar"
        >
          <ChevronsLeft className={`w-4 h-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </aside>

      {/* Mobile sidebar drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/40" onClick={() => setMobileOpen(false)}>
          <div className="absolute inset-y-0 left-0 w-64 bg-card border-r" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 flex items-center gap-2 px-4 border-b">
              <div className="w-8 h-8 rounded-lg bg-blue-600 grid place-items-center"><Zap className="w-5 h-5 text-white" strokeWidth={2.5} /></div>
              <span className="font-heading font-semibold">RushGro</span>
            </div>
            <nav className="p-2 space-y-0.5">
              {items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.to === "/dashboard"} onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-blue-600 text-white" : "hover:bg-muted"}`}>
                  <it.icon className="w-4 h-4" /><span>{it.label}</span>
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Main */}
      <div className={`relative z-10 flex-1 flex flex-col min-w-0 ${collapsed ? "md:ml-16" : "md:ml-60"}`}>
        {/* Header */}
        <header className="sticky top-0 z-20 h-14 glass-header flex items-center px-4 gap-3">
          <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu className="w-5 h-5" />
          </Button>
          <div className="text-sm flex items-center gap-1.5 text-muted-foreground min-w-0 overflow-hidden">
            {crumbs.map((c, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="opacity-50">/</span>}
                <span className={i === crumbs.length - 1 ? "text-foreground font-medium" : ""}>{c.label}</span>
              </React.Fragment>
            ))}
          </div>
          <div className="flex-1" />
          <div className="hidden md:flex items-center gap-2 max-w-sm w-full">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input placeholder="Search…" className="pl-9 h-9 bg-muted/40 border-muted-foreground/10" />
            </div>
          </div>
          <Button variant="ghost" size="icon" data-testid={TID.themeToggle} onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label="Toggle theme">
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="icon" data-testid={TID.notifBell} onClick={() => nav("/notifications")} aria-label="Notifications">
            <Bell className="w-4 h-4" />
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid={TID.userMenu} className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-muted transition-colors">
                <Avatar className="w-7 h-7"><AvatarFallback className="bg-blue-600 text-white text-xs">{user.name?.[0] || "U"}</AvatarFallback></Avatar>
                <div className="text-left hidden sm:block">
                  <div className="text-xs font-medium leading-tight">{user.name}</div>
                  <div className="text-[10px] text-muted-foreground">{ROLE_LABEL[user.role]}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/settings")}>Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={TID.logoutBtn} onClick={doLogout} className="text-red-600 focus:text-red-600">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 md:p-6 space-y-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
